import { useParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { metricMaxVal, MetricTypeConstants } from "../../constants/MetricTypeConstrants";
import { MatrixCard } from "../../components/cards/MatrixCard";

import { Chart, ChartData } from "../../components/Chart";
import { createPatientApi } from "../../api-helpers/PatientApiHelper";
import { MetricData, MetricRequest, MetricResponse, PatientPersonalInfo } from "patients-experience-client";
import { formatDate} from "../../helper/dateFormatter";
import Spinner from "../../components/Spinner";
import { formatDateTime, formatDateTimeZone, getTimezonedAbbreviations } from "../schedule/dateFormat";
import { useDispatch } from "react-redux";
import { setPageTitle } from "../../store/slices/pageTitleSlice";
import { AddMetrics } from "../Appointment/AddMetrics";
export class MatrixConstants {
    public static readonly HEIGHT = 'Height'; 
    public static readonly WEIGHT = 'Weight';
    public static readonly BMI = 'BMI';
    public static readonly O2_READING = 'SpO2';
    public static readonly PULSE = 'BPM';
    public static readonly A1C = 'A1C';
    public static readonly BLOOD_SUGAR = 'BloodSugar';
    public static readonly BLOOD_PRESSURE_SYSTOLIC = 'BloodPressureSystolic';
    public static readonly BLOOD_PRESSURE_DIASTOLIC = 'BloodPressureDiastolic';
    public static readonly STEPS_COUNT = 'StepsCount';
    public static readonly STS = 'STS';
    public static readonly FEV1_ACTUAL = 'FEV1';
    public static readonly FEV1_PREDICTED = 'FEV1Predicted';
    public static readonly PEAK_FLOW_ACTUAL = 'PeakFlow';
    public static readonly PEAK_FLOW_PREDICTED = 'PeakFlowPredicted';
}

export const Matrix = () => {
    const heightRef = useRef<HTMLDivElement>(null);
    const oxygenRef = useRef<HTMLDivElement>(null);
    const pulseRef = useRef<HTMLDivElement>(null);
    const lungFunctionRef = useRef<HTMLDivElement>(null);
    const sitStandRef = useRef<HTMLDivElement>(null);
    const bpRef = useRef<HTMLDivElement>(null);
    const a1cRef = useRef<HTMLDivElement>(null);

    const patientApi = createPatientApi();
    const { patientId } = useParams<{ patientId: string }>();

    const [metricsLatestData, setMetricsLatestData] = useState<Map<string, MetricResponse>>(new Map());
    const [metricsAllData, setMetricsAllData] = useState<Map<string, MetricResponse[]>>(new Map());
    const [chartGridData, setChartGridData] = useState<Map<string, ChartData[]>>(new Map());

    // Grid and Charts Data
    const [bmiData, setBmiData] = useState<ChartData[]>([]);
    const [fev1Data, setFev1Data] = useState<ChartData[]>([]);
    const [O2Data, setO2Data] = useState<ChartData[]>([]);
    const [pulseData, setPulseData] = useState<ChartData[]>([]);
    const [peakFlowData, setPeakFlowData] = useState<ChartData[]>([]);
    const [stsData, setStsData] = useState<ChartData[]>([]);
    const [stepsData, setStepsData] = useState<ChartData[]>([]);
    const [bpData, setBpData] = useState<ChartData[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [elevatedReadingsCount, setElevatedReadingsCount] = useState<number>(0);
    const [elevatedReadings, setElevatedReadings] = useState<string>('');
    const [showModal, setShowModal] = useState(false);
    const [refreshMetrics, setRefreshMetrics] = useState<number>(0);

    const [patientInfo , setPatientInfo] = useState<PatientPersonalInfo>();
    const dispatch = useDispatch();
    const handleScroll = (ref: React.RefObject<HTMLDivElement>, e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        e.preventDefault();
        ref.current?.scrollIntoView({ behavior: 'smooth' })
    }

    const fetchMetricsLatestData = async (metricType:string) => {
        try {
            setLoading(true);
            const metricResponse= await patientApi.patientsControllerGetMetricLatestData({}, patientId!, metricType)
            const metric = metricResponse.data;
            setMetricsLatestData(prev => new Map(prev).set(metricType, metric));
        } catch (error) {
            console.log('error : ', error);
        } finally {
            setLoading(false);
        }
    }

    const fetchMetricsAllData = async (metricType:string) => {
        try {
            setLoading(true);
            const requestBody:MetricRequest = {}
        
            if(metricType !== MatrixConstants.BMI) {
                const date30YearsAgo = new Date();
                date30YearsAgo.setFullYear(date30YearsAgo.getFullYear() - 30);

                requestBody['fromDate'] = date30YearsAgo;
                requestBody['toDate'] = new Date();
            }

            const metricResponse= await patientApi.patientsControllerGetMetricData(requestBody, patientId!, metricType)
            const metrics = metricResponse.data;

            if (metricType === MatrixConstants.BLOOD_PRESSURE_SYSTOLIC) {
                let readings: number = 0;
                const date30DaysAgo = new Date();
                date30DaysAgo.setDate(date30DaysAgo.getDate() - 30);

                let filteredData = metrics.filter(item => {
                    const measuredDate = new Date(item.measuredAt);
                    return measuredDate >= date30DaysAgo;
                });

                filteredData = filteredData?.reverse();
                filteredData.forEach((metric: MetricResponse) => {
                    let sys: number = 0;
                    let dia: number = 0;
                    let isElevated: boolean = false;
                    metric.metricData.forEach((data: MetricData) => {
                        if (data.metricType === MatrixConstants.BLOOD_PRESSURE_SYSTOLIC) {
                            sys = data.metricValue;
                        } else if (data.metricType === MatrixConstants.BLOOD_PRESSURE_DIASTOLIC) {
                            dia = data.metricValue;
                        }                        
                    });
                    if (sys > metricMaxVal.get(MetricTypeConstants.BLOOD_PRESSURE_SYSTOLIC)! || dia > metricMaxVal.get(MetricTypeConstants.BLOOD_PRESSURE_DIASTOLIC)!) {
                        readings++;
                        isElevated = true;
                    }
                    if (isElevated) {
                        setElevatedReadings(`${sys}/${dia}`);
                    }
                });
                setElevatedReadingsCount(readings)
            }

            setMetricsAllData(prev => new Map(prev).set(metricType, metrics));
            // console.log(`${metricType} data : `, metric);
        } catch (error) {
            console.log('error : ', error);
        } finally {
            setLoading(false);
        }
    }

    const getMetricValue = (metricType: string) : number=> {
        // debugger
        const data = metricsLatestData.get(metricType)?.metricData;
        const dataObject = data?.find((m) => m.metricType === metricType);
        if (dataObject?.metricValue) {
            return Number(Number(dataObject?.metricValue)?.toFixed(2));
        }
        else {
            return 0;
        }
    }

    const getMetricValueLessThan85 = (metricType: string) : number => {
        const metrics = metricsAllData.get(metricType);
        return metrics ? metrics.filter(metric => 
            metric.metricData[0]?.metricValue <= 85
        ).length : 0;
    };

    const getMetricValueBetween = (metricType: string, start: number, end: number) : number => {
        const metrics = metricsAllData.get(metricType);
        return metrics?.filter(metric =>
            metric.metricData[0]?.metricValue >= start && metric.metricData[0]?.metricValue <= end
        ).length ?? 0;
    };

    const getMaxValue = (metricType: string) : number => {
        const metrics = metricsAllData.get(metricType);
        return metrics?.reduce((max, metric) => {
            const metricValue = metric.metricData[0]?.metricValue;
            return metricValue && metricValue > max ? metricValue : max;
        }, 0) ?? 0;
    };

    const getAverageValue = (metricType: string): number => {
        const metrics = metricsAllData.get(metricType);
        if (!metrics || metrics.length === 0) return 0;
        
        const values = metrics
            .map(metric => Number(metric.metricData[0]?.metricValue) || 0)
            .filter(value => !isNaN(value));
            
        const sum = values.reduce((acc, val) => acc + val, 0);
        return values.length > 0 ? Math.round(sum / values.length) : 0;
    };

    const getDifferenceOfLast2Readings = (metricType: string) : number => {
        const metrics = metricsAllData.get(metricType);
        if (metrics && metrics.length >= 2) {
            const lastMetric = metrics[0];
            const secondLastMetric = metrics[1];
            const lastMetricValue = lastMetric.metricData[0]?.metricValue;
            const secondLastMetricValue = secondLastMetric.metricData[0]?.metricValue;
            return lastMetricValue - secondLastMetricValue;
        }
        return 0;
    };

    const getDifferencePercentageOfLast2Readings = (metricType: string) : number => {
        const metrics = metricsAllData.get(metricType);
        if (metrics && metrics.length >= 2) {
            const lastMetric = metrics[0];
            const secondLastMetric = metrics[1];
            const lastMetricValue = lastMetric.metricData[0]?.metricValue;
            const secondLastMetricValue = secondLastMetric.metricData[0]?.metricValue;
            const difference = lastMetricValue - secondLastMetricValue;
            const percentage = (difference / secondLastMetricValue) * 100;
            return Math.round(percentage);
        }
        return 0;
    };

    const getLastUpdated = (metricType: string): string => {
        const metric = metricsLatestData.get(metricType)?.measuredAt;
        const newDate = new Date(metric!);
        if (metric) {
            return newDate.toISOString();
        }
        return '';
    }

    const getPatientInfo = async () => {
      setLoading(true);

      try {
        const response =
          await patientApi.patientsControllerGetPatientPersonalInfo(
            patientId || ""
          );
        setPatientInfo(response.data);
      } catch (error: any) {
        console.error("Error fetching patient information:", error);

        const errorMessage =
          error?.response?.data?.message ||
          "Failed to fetch patient information. Please try again later.";

        console.log(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
        dispatch(setPageTitle('Metrics: Patient Profile | Spire360'));
        getPatientInfo();
        Object.values(MatrixConstants).forEach(metricType => {
            fetchMetricsLatestData(metricType);
            fetchMetricsAllData(metricType);
        });
    }, [refreshMetrics])

    const formatDayMonth = (date: string | number) => {
        const newDate = new Date(date);
        return newDate.toLocaleDateString('en-US', {
            day: '2-digit',
            month: 'short'
          }).replace(/ /g, ' ');
    }

    const mapData = (metricType: string) => {
        const metrics = metricsAllData?.get(metricType);
        const mappedData: ChartData[] = [];
        
        if (!metrics?.length) return;

        if (metrics && Array.isArray(metrics)) {
            // Get unique metric types from first record's metric data
            const uniqueMetricTypes = Array.from(
                new Set(metrics.flatMap(m => m.metricData.map(metric => metric.metricType)))
            );
            let count = 0 
            metrics?.forEach(metric => {
                count++;
                const dataPoint: { [key: string]: string | number } = 
                metricType === MatrixConstants.PULSE || metricType === MatrixConstants.O2_READING ? {
                    day: formatDate(metric.measuredAt),
                    Day: formatDateTimeZone(metric?.measuredAt?.toString(), getTimezonedAbbreviations(patientInfo?.timeZone || '')),
                } : {
                    day: formatDate(metric.measuredAt),
                };

                if(metricType === MatrixConstants.FEV1_ACTUAL || metricType === MatrixConstants.PEAK_FLOW_ACTUAL) {
                        dataPoint['test'] = Number(count);
                }
    
                // Add each unique metric type value to the data point
                uniqueMetricTypes?.forEach(type => {
                    const value = metric.metricData.find(m => m.metricType === type)?.metricValue;
                    const source = metric.metricData.find(m => m.metricType === type)?.source;
                    if (value) {
                        dataPoint[type] = Number(value);
                    }

                    if(metricType === MatrixConstants.O2_READING || metricType === MatrixConstants.O2_READING) {
                        dataPoint['source'] = source ?? '';
                    }
                });
    
                mappedData.push(dataPoint);
            });
        }
    
        return mappedData;
    };
    

    useEffect(() => {

        const bmiMappedData = mapData(MatrixConstants.BMI)
        if(bmiMappedData) {
            setChartGridData(prev => new Map(prev).set(MatrixConstants.BMI, bmiMappedData));

            
            // map day, weight and bmi from bmiMappedData object to a new array 
            const bmiDayWeightData = bmiMappedData.map((data) => ({
                day: new Date(data.day).toLocaleDateString('en-US', { month: 'short' }),
                weight:  data[MatrixConstants.WEIGHT],
                // height:  data[MatrixConstants.HEIGHT],
                bmi: data[MatrixConstants.BMI]
            }));
            setBmiData(bmiDayWeightData?.reverse()); 
        }

        const date7DaysAgo = new Date();
        date7DaysAgo.setDate(date7DaysAgo.getDate() - 7);

        const oxygenMappedData = mapData(MatrixConstants.O2_READING);
        if(oxygenMappedData){
            setChartGridData(prev => new Map(prev).set(MatrixConstants.O2_READING, oxygenMappedData));
            const filteredData = oxygenMappedData.filter(item => {
                const measuredOn = new Date(item.day);
                return measuredOn >= date7DaysAgo;
            });
            setO2Data(filteredData);
        }

        const pulseMappedData = mapData(MatrixConstants.PULSE);
        if(pulseMappedData){
            setChartGridData(prev => new Map(prev).set(MatrixConstants.PULSE, pulseMappedData));
            const filteredData = pulseMappedData.filter(item => {
                const measuredOn = new Date(item.day);
                return measuredOn >= date7DaysAgo;
            });
            setPulseData(filteredData);
        }

        const fevMappedData = mapData(MatrixConstants.FEV1_ACTUAL);
        if(fevMappedData){
            setChartGridData(prev => new Map(prev).set(MatrixConstants.FEV1_ACTUAL, fevMappedData));
            const fevData = fevMappedData.map((data) => ({
                'Test': data.test,
                'FEV1 (Actual)': data[MatrixConstants.FEV1_ACTUAL],
                'FEV1 (Predicted)': data[MatrixConstants.FEV1_PREDICTED],
            }));
            setFev1Data(fevData);

            const peakflowData = fevMappedData.map((data) => ({
                'Test': data.test,
                'Peak Flow Actual (L/min)': data[MatrixConstants.PEAK_FLOW_ACTUAL],
                'Peak Flow Predicted (L/min)': data[MatrixConstants.PEAK_FLOW_PREDICTED],
            }));
            setPeakFlowData(peakflowData);
        }

        const stsMappedData = mapData(MatrixConstants.STS);
        if(stsMappedData){
            setChartGridData(prev => new Map(prev).set(MatrixConstants.STS, stsMappedData));
            const filteredData = stsMappedData.filter(item => {
                const measuredOn = new Date(item.day);
                return measuredOn >= date7DaysAgo;
            });
            setStsData(filteredData);
        }

        const stepsMappedData = mapData(MatrixConstants.STEPS_COUNT);
        if(stepsMappedData){
            setChartGridData(prev => new Map(prev).set(MatrixConstants.STEPS_COUNT, stepsMappedData));
            const filteredData = stepsMappedData.filter(item => {
                const measuredOn = new Date(item.day);
                return measuredOn >= date7DaysAgo;
            });
            setStepsData(filteredData);
        }

        const bloodPressureMappedData = mapData(MatrixConstants.BLOOD_PRESSURE_SYSTOLIC);
        if(bloodPressureMappedData){
            setChartGridData(prev => new Map(prev).set(MatrixConstants.BLOOD_PRESSURE_SYSTOLIC, bloodPressureMappedData));
            const filteredData = bloodPressureMappedData.filter(item => {
                const measuredOn = new Date(item.day);
                return measuredOn >= date7DaysAgo;
            });

            const bpData = filteredData.map((data, index: number) => ({
                'Day': data.day,
                'Systolic (upper)': data[MatrixConstants.BLOOD_PRESSURE_SYSTOLIC],
                'Diastolic (lower)': data[MatrixConstants.BLOOD_PRESSURE_DIASTOLIC],
            }));
            setBpData(bpData);
        }

        const a1cMappedData = mapData(MatrixConstants.A1C);
        if(a1cMappedData){
            setChartGridData(prev => new Map(prev).set(MatrixConstants.A1C, a1cMappedData));
        }

    }, [metricsAllData])
    


    return(
    <>
        <AddMetrics
              patientId={patientId!}
              showModal={showModal}
              handleModal={() => setShowModal(!showModal)}
              refreshMetrics={() => {setRefreshMetrics(refreshMetrics + 1)}}
        />
        {loading ? <Spinner /> : 
        <div className="d-md-flex">
            <ul className="scroll-list d-flex gap-4 align-items-center flex-column">
                <div className="w-100">
                    <li className="scroll-list__item">
                        <a href="#" className="scroll-list__link" onClick={(e) => handleScroll(heightRef, e)}>Height, Weight, BMI</a>
                    </li>
                    <li className="scroll-list__item">
                        <a href="#" className="scroll-list__link" onClick={(e) => handleScroll(oxygenRef, e)}>Oxygen Saturation</a>
                    </li>
                    {/* <li className="scroll-list__item">
                        <a href="#" className="scroll-list__link" onClick={(e) => handleScroll(pulseRef, e)}>Pulse</a>
                    </li> */}
                    <li className="scroll-list__item">
                        <a href="#" className="scroll-list__link" onClick={(e) => handleScroll(lungFunctionRef, e)}>Lung Function</a>

                    </li>
                    <li className="scroll-list__item">
                        <a href="#" className="scroll-list__link" onClick={(e) => handleScroll(sitStandRef, e)}>Sit to Stand, Steps</a>
                    </li>
                    <li className="scroll-list__item">
                        <a href="#" className="scroll-list__link" onClick={(e) => handleScroll(bpRef, e)}>Blood Pressure</a>
                    </li>
                    <li className="scroll-list__item">
                        <a href="#" className="scroll-list__link" onClick={(e) => handleScroll(a1cRef, e)}>A1C</a>
                    </li>
                </div>
                <button
                    className="btn btn-sm btn-outline-secondary rounded-pill ps-3 pe-4 py-2 fs-13 self-align-center fw-semibold btn-shadow"
                    type="submit" onClick={() => setShowModal(!showModal)}
                >
                    <span className="material-symbols-outlined fs-16 align-text-top fw-semibold">
                        add
                    </span>
                    Add Metrics
                </button>
            </ul>
            <div className="tabs__content flex-fill">
                <div className="p-5 bg-gray-FAF rounded-3 mb-5" ref={heightRef}>
                <h1 className="h4">Height, Weight, BMI</h1>
                <div className="row g-3 mb-4">
                    <div className="col-12 col-md-12 col-lg-9 col-xl-7">
                        <div className="row g-3">
                            <MatrixCard 
                                type="HEIGHT" 
                                value={getMetricValue(MatrixConstants.HEIGHT)} 
                                lastUpdated={getLastUpdated(MatrixConstants.HEIGHT)} 
                                predicted={undefined}
                            />
                            <MatrixCard 
                                type="WEIGHT" 
                                value={getMetricValue(MatrixConstants.WEIGHT)} 
                                lastUpdated={getLastUpdated(MatrixConstants.WEIGHT)} 
                                predicted={undefined} 
                            />
                            <MatrixCard 
                                type="BMI" 
                                value={getMetricValue(MatrixConstants.BMI)}
                                lastUpdated={getLastUpdated(MatrixConstants.BMI)} 
                                predicted={undefined} 
                            />
                        </div>
                    </div>
                </div>
                <div className="row g-3">    
                    <div className="col-12 col-lg-7">
                            {/* <img className="img-fluid" src={chartSample}/> */}
                            { chartGridData.get(MatrixConstants.BMI)?.length ?
                                <div className="border rounded-3 shadow-sm bg-white">
                                <Chart 
                                    data={bmiData}
                                    secondaryAxisKeys={['bmi']}
                                    secondaryAxisLabel="BMI"
                                    yLabel="Weight(lbs)"
                                    type="line"
                                    xAxisKey="day"
                                    showLegend={true}
                                    legendNames={{weight: "Weight(lbs)", bmi: "BMI"}}
                                    legendPosition="bottom"
                                    showTooltip={true}
                                    title=""
                                /> </div>: ''
                            }
                    </div>

                    {/* Map BMi Data in the below table */}
                    <div className="col-12 col-lg-5">
                        <div className="sidebar">
                            <div className="sidebar__header bg-white">
                                <h3 className="text-primary fs-20 m-0">BMI</h3>
                                <div className="fs-11 text-light">Last Updated: {
                                    getLastUpdated(MatrixConstants.BMI) ? formatDateTime(getLastUpdated(MatrixConstants.BMI)) : '-'
                                }</div>
                            </div>
                                <div className="bg-white rounded p-0 fs-12">
                                    <div className="d-flex p-3 bg-teal-light">
                                        <strong className="w-40">Date</strong>
                                        <strong className="w-20 text-center">Weight (lbs)</strong>
                                        <strong className="w-20 text-center">Height (in)</strong>
                                        <strong className="w-20 text-center">BMI</strong>
                                    </div>
                                    <hr className="m-0" />
                                    <div className="sidebar__content p-0 overflow-y-auto max-h-16">
                                        {
                                            chartGridData.get(MatrixConstants.BMI)?.length ? chartGridData.get(MatrixConstants.BMI)?.map((item, index) => (
                                                <div key={index} className="d-flex d-block p-3">
                                                    <span className="w-40">{item.day}</span>
                                                    <span className="w-20 text-center">{item[MatrixConstants.WEIGHT]}</span>
                                                    <span className="w-20 text-center">{item[MatrixConstants.HEIGHT]}</span>
                                                    <span className="w-20 text-center">{item[MatrixConstants.BMI]}</span>
                                                </div>
                                            )) : <div className="d-flex d-block p-3">
                                            <span className="w-100 fw-bold text-center">No Data Found</span>
                                        </div>
                                        }
                                    </div>
                                </div>
                        </div>
                    </div>
                </div></div>
                <div className="p-5 bg-gray-FAF rounded-3 mb-5" ref={oxygenRef}>
                <h1 className="h4">Oxygen Saturation</h1>
                <div className="row g-3 mb-4">
                    <div className="col-12 col-md-12 col-lg-9 col-xl-7">
                        <div className="row g-3">
                            <MatrixCard 
                                type="O2_READING" 
                                value={getMetricValue(MatrixConstants.O2_READING)}
                                range=" "
                                lastUpdated={getLastUpdated(MatrixConstants.O2_READING)} 
                                predicted={undefined} 
                            />
                            <MatrixCard 
                                type="EMERGENT_READINGS" 
                                value={getMetricValueLessThan85(MatrixConstants.O2_READING)}
                                range="<= 85%"
                                predicted={undefined} 
                            />
                            <MatrixCard 
                                type="URGENT_READINGS" 
                                value={getMetricValueBetween(MatrixConstants.O2_READING, 86, 87)}
                                range="86-87 %"
                                lastUpdated={getLastUpdated(MatrixConstants.O2_READING)} 
                                predicted={undefined} 
                            />
                        </div>
                    </div>
                </div>
                <div className="row g-3">    
                    <div className="col-12 col-lg-7">
                        { chartGridData.get(MatrixConstants.O2_READING)?.length ?
                            <div className="border rounded-3 shadow-sm bg-white">
                            <Chart 
                                data={[...O2Data!]?.reverse()}
                                type="scatter"
                                xAxisKey="day"
                                xLabel="Last 7 days"
                                yLabel="O2 Saturation"
                                showTooltip={true}
                                title=""
                            /> </div>: ''
                        }
                    </div>
                    <div className="col-12 col-lg-5">
                        <div className="sidebar fs-12">
                            <div className="d-flex p-3 bg-teal-light">
                                <strong className="w-40"> Date </strong>
                                <strong className="w-30"> O2 Saturation </strong>
                                <strong className="w-30"> Source </strong>
                            </div>
                            <hr className="m-0" />
                            <div className="sidebar__content p-0 overflow-y-auto max-h-16">
                                {
                                    chartGridData.get(MatrixConstants.O2_READING)?.length ? chartGridData.get(MatrixConstants.O2_READING)?.map((item, index) => (
                                        <div key={index} className="d-flex d-block p-3">
                                            <span className="w-40">{item.Day}</span>
                                            <span className="w-30 text-center">{item[MatrixConstants.O2_READING]}</span>
                                            <span className="w-30">{item.source}</span>
                                        </div>
                                    )) : <div className="d-flex d-block p-3">
                                    <span className="w-100 fw-bold text-center">No Data Found</span>
                                </div>
                                }
                            </div>
                        </div>
                    </div>
                </div>
                <div className="pt-5" ref={pulseRef}>
                <h1 className="h4">Pulse</h1>
                <div className="row g-3 mb-4">
                    <div className="col-12 col-md-12 col-lg-9 col-xl-7">
                        <div className="row g-3">
                            <MatrixCard 
                                type="PULSE" 
                                value={getMetricValue(MatrixConstants.PULSE)} 
                                lastUpdated={getLastUpdated(MatrixConstants.PULSE)} 
                                predicted={undefined} 
                            />
                        </div>
                    </div>
                </div>
                <div className="row g-3">    
                    <div className="col-12 col-lg-7">
                        { chartGridData.get(MatrixConstants.PULSE)?.length ?
                            <div className="border rounded-3 shadow-sm bg-white">
                            <Chart 
                                data={[...pulseData!]?.reverse()}
                                type="scatter"
                                xAxisKey="Day"
                                xLabel="Last 7 days"
                                yLabel="Pulse"
                                showTooltip={true}
                                title=""
                            /> </div>: ''
                        }
                    </div>
                    <div className="col-12 col-lg-5">
                        <div className="sidebar fs-12">
                            <div className="sidebar__header">
                                <div className="d-flex">
                                    <strong className="w-80"> Date </strong>
                                    <strong className="w-20 text-center">Pulse</strong>
                                </div>
                            </div>
                            <div className="sidebar__content p-0 overflow-y-auto max-h-16">
                                {
                                    chartGridData.get(MatrixConstants.PULSE)?.length ? chartGridData.get(MatrixConstants.PULSE)?.map((item, index) => (
                                        <div key={index} className="d-flex d-block p-3">
                                            <span className="w-80">{item.Day}</span>
                                            <span className="w-20 text-center">{item[MatrixConstants.PULSE]}</span>
                                        </div>
                                    )) : <div className="d-flex d-block p-3">
                                    <span className="w-100 fw-bold text-center">No Data Found</span>
                                </div>
                                }
                            </div>
                        </div>
                    </div>
                </div>
                </div>
                </div>
                <div className="p-5 bg-gray-FAF rounded-3 mb-5" ref={lungFunctionRef}>
                <h1 className="h4 mb-3">Lung Function</h1>
                <h3 className="h6">FEV1 (Actual vs Predicted)</h3>

                <div className="row g-3 mb-4">
                    <div className="col-12 col-md-12 col-lg-9 col-xl-7">
                        <div className="row g-3">
                            <MatrixCard 
                                type="FEV1" 
                                value={getMetricValue(MatrixConstants.FEV1_ACTUAL)} 
                                predicted={getMetricValue(MatrixConstants.FEV1_PREDICTED)} 
                                lastUpdated={getLastUpdated(MatrixConstants.FEV1_ACTUAL)} 
                            />
                        </div>
                    </div>
                </div>
                <div className="row g-3">    
                    <div className="col-12 col-lg-7">
                        { chartGridData.get(MatrixConstants.FEV1_ACTUAL)?.length ?
                            <div className="border p-4 rounded-3 shadow-sm bg-white">
                            <Chart 
                                data={fev1Data}
                                type="line"
                                showLegend={true}
                                xAxisKey="Test"
                                yLabel="Liters"
                                xLabel="Test"
                                showTooltip={true}
                                title=""
                            /> </div>: ''
                        }
                    </div>
                    <div className="col-12 col-lg-5">
                        <div className="sidebar fs-12">
                            <div className="sidebar__header">
                                <div className="d-flex">
                                    <strong className="w-10"> Test </strong>
                                    <strong className="w-20 text-center">FEV1</strong>
                                    <strong className="w-25 text-center">FEV1 Predicted</strong>
                                    <strong className="w-20 text-center">{'Peak Flow Actual(L/min)'}</strong>
                                    <strong className="w-25 text-center">{'Peak Flow Predicted(L/min)'}</strong>
                                </div>
                            </div>
                            <div className="sidebar__content p-0 overflow-y-auto max-h-16">
                                {
                                    chartGridData.get(MatrixConstants.FEV1_ACTUAL)?.length ? chartGridData.get(MatrixConstants.FEV1_ACTUAL)?.map((item, index) => (
                                        <div key={index} className="d-flex d-block p-3">
                                            <span className="w-10">{item.test}</span>
                                            <span className="w-20 text-center">{item[MatrixConstants.FEV1_ACTUAL]}</span>
                                            <span className="w-25 text-center">{item[MatrixConstants.FEV1_PREDICTED]}</span>
                                            <span className="w-20 text-center">{item[MatrixConstants.PEAK_FLOW_ACTUAL]}</span>
                                            <span className="w-25 text-center">{item[MatrixConstants.PEAK_FLOW_PREDICTED]}</span>
                                        </div>
                                    )) : <div className="d-flex d-block p-3">
                                    <span className="w-100 fw-bold text-center">No Data Found</span>
                                </div>
                                }
                            </div>
                        </div>
                    </div>
                </div>
                <div>
                <h3 className="h6 mt-4">Peak Flow (Actual Vs Predicted)</h3>
                <div className="row g-3 mb-4">
                    <div className="col-12 col-md-12 col-lg-9 col-xl-7">
                        <div className="row g-3">
                            <MatrixCard 
                                type="PEAK_FLOW" 
                                value={getMetricValue(MatrixConstants.PEAK_FLOW_ACTUAL)} 
                                predicted={getMetricValue(MatrixConstants.PEAK_FLOW_PREDICTED)} 
                                lastUpdated={getLastUpdated(MatrixConstants.PEAK_FLOW_ACTUAL)} 
                            />
                        </div>
                    </div>
                </div>
                <div className="row g-3">    
                <div className="col-12 col-lg-7">
                    { chartGridData.get(MatrixConstants.FEV1_ACTUAL)?.length ?
                    <div className="border p-4 rounded-3 shadow-sm bg-white">
                        <Chart 
                            data={peakFlowData}
                            type="line"
                            xAxisKey="Test"
                            yLabel="Liters"
                            showLegend={true}
                            showTooltip={true}
                            title=""
                        /> </div>: ''
                    }
                </div>
                </div>
                </div>                
                </div>
                <div className="p-5 bg-gray-FAF rounded-3 mb-5" ref={sitStandRef}>
                <h1 className="h4">Sit to Stand</h1>
                <div className="row g-3 mb-4">
                    <div className="col-12 col-md-12 col-lg-9 col-xl-7">
                        <div className="row g-3">
                            <MatrixCard 
                                type="STS" 
                                value={getMetricValue(MatrixConstants.STS)}
                                predicted={undefined}
                                lastUpdated={getLastUpdated(MatrixConstants.STS)} 
                            />
                            <MatrixCard 
                                type="MAX_STS_ALL_TIME" 
                                value={getMaxValue(MatrixConstants.STS)}
                                predicted={undefined}
                            />
                            <MatrixCard 
                                type="DIFF_SINCE_LAST_STS" 
                                value={Math.abs(getDifferenceOfLast2Readings(MatrixConstants.STS))}
                                trend={getDifferenceOfLast2Readings(MatrixConstants.STS) > 0 ? 'up' : 'down'}
                                predicted={undefined}
                                lastUpdated={getLastUpdated(MatrixConstants.STS)}
                            />
                        </div>
                    </div>
                </div>
                <div className="row g-3 mb-4">    
                    <div className="col-12 col-lg-7">
                        { chartGridData.get(MatrixConstants.STS)?.length ?
                        <div className="border p-4 rounded-3 shadow-sm bg-white">
                            <Chart 
                                data={[...stsData!]?.reverse()}
                                type="line"
                                xAxisKey="day"
                                yLabel="Reps"
                                showTooltip={true}
                                title=""
                            />
                        </div> : ''
                        }
                    </div>
                    <div className="col-12 col-lg-5">
                        <div className="sidebar fs-12 mb-5">
                            <div className="sidebar__header">
                                <div className="d-flex">
                                    <strong className="w-50">Date</strong>
                                    <strong className="w-50 text-center">Sit to Stand (rep)</strong>
                                </div>
                            </div>
                            <div className="sidebar__content p-0 overflow-y-auto max-h-16">
                                {
                                    chartGridData.get(MatrixConstants.STS)?.length ? chartGridData.get(MatrixConstants.STS)?.map((item, index) => (
                                        <>
                                            <div key={index} className="d-flex d-block p-3">
                                                <span className="w-50">{item.day}</span>
                                                <span className="w-50 text-center">{item[MatrixConstants.STS]}</span>
                                            </div>
                                            <hr className="m-0" />
                                        </>
                                    )) : <div className="d-flex d-block p-3">
                                    <span className="w-100 fw-bold text-center">No Data Found</span>
                                </div>
                                }
                            </div>
                        </div>
                    </div>
                </div>
                <div>
                {/* <h1 className="h5 mt-4">Steps</h1> */}
                <div className="row g-3 mb-4">
                    <div className="col-12 col-md-12 col-lg-9 col-xl-7">
                        <div className="row g-3">
                            <MatrixCard 
                                type="STEPS_COUNT" 
                                value={getMetricValue(MatrixConstants.STEPS_COUNT)}
                                predicted={undefined}
                                lastUpdated={getLastUpdated(MatrixConstants.STEPS_COUNT)} 
                            />
                            <MatrixCard 
                                type="AVERAGE_STEPS_COUNT" 
                                value={getAverageValue(MatrixConstants.STEPS_COUNT)}
                                predicted={undefined}
                            />
                        </div>
                    </div>
                </div>
                <div className="row g-3">    
                    <div className="col-12 col-lg-7">
                        { chartGridData.get(MatrixConstants.STEPS_COUNT)?.length ?
                            <div className="border p-4 rounded-3 shadow-sm bg-white">
                            <Chart 
                                data={[...stepsData!]?.reverse()}
                                type="bar"
                                xAxisKey="day"
                                yLabel="Steps"
                                showTooltip={true}
                                title=""
                            /> </div> : ''
                        }
                    </div>
                    <div className="col-12 col-lg-5">
                        <div className="sidebar fs-12">
                            <div className="sidebar__header">
                                <div className="d-flex">
                                    <strong className="w-50"> Date </strong>
                                    <strong className="w-50 text-center"> Steps Count </strong>
                                </div>
                            </div>
                            <div className="sidebar__content p-0 overflow-y-auto max-h-16">
                                {
                                    chartGridData.get(MatrixConstants.STEPS_COUNT)?.length ? chartGridData.get(MatrixConstants.STEPS_COUNT)?.map((item, index) => (
                                        <>
                                            <div key={index} className="d-flex d-block p-3">
                                                <span className="w-50">{item.day}</span>
                                                <span className="w-50 text-center">{item[MatrixConstants.STEPS_COUNT]}</span>
                                            </div>
                                            <hr className="m-0" />
                                        </>
                                    )) : <div className="d-flex d-block p-3">
                                    <span className="w-100 fw-bold text-center">No Data Found</span>
                                </div>
                                }
                            </div>
                        </div>
                    </div>
                </div>
                </div>
                </div>
                <div className="p-5 bg-gray-FAF rounded-3 mb-5" ref={bpRef}>
                <h1 className="h4">Blood Pressure</h1>
                <div className="row g-3 mb-4">
                    <div className="col-12 col-md-12 col-lg-9 col-xl-7">
                        <div className="row g-3">
                            <MatrixCard 
                                type="BLOOD_PRESSURE_SYSTOLIC" 
                                value={getMetricValue(MatrixConstants.BLOOD_PRESSURE_SYSTOLIC)}
                                predicted={getMetricValue(MatrixConstants.BLOOD_PRESSURE_DIASTOLIC)}
                                lastUpdated={getLastUpdated(MatrixConstants.BLOOD_PRESSURE_SYSTOLIC)} 
                            />
                            <MatrixCard 
                                type="ELEVATED_READINGS" 
                                value={elevatedReadingsCount}
                                predicted={undefined}
                                elevatedReadings={elevatedReadings}
                            />
                        </div>
                    </div>
                </div>
                <div className="row g-3">    
                    <div className="col-12 col-lg-7">
                        { chartGridData.get(MatrixConstants.BLOOD_PRESSURE_SYSTOLIC)?.length ?
                            <div className="border p-4 rounded-3 shadow-sm bg-white">
                            <Chart 
                                data={[...bpData]?.reverse()}
                                type="line"
                                xAxisKey="Day"
                                yLabel="Blood Pressure (mm/Hg)"
                                showTooltip={true}
                                showLegend={true}
                                title=""
                            /> </div>: ''
                        }
                    </div>
                    <div className="col-12 col-lg-5">
                        <div className="sidebar fs-12">
                            <div className="sidebar__header">
                                <div className="d-flex">
                                    <strong className="w-30">Date</strong>
                                    <strong className="w-35 text-center">Systolic (Upper)</strong>
                                    <strong className="w-35 text-center">Diastolic (Lower)</strong>
                                </div>
                            </div>
                            <div className="sidebar__content p-0 overflow-y-auto max-h-16">
                                {
                                    chartGridData.get(MatrixConstants.BLOOD_PRESSURE_SYSTOLIC)?.length ? chartGridData.get(MatrixConstants.BLOOD_PRESSURE_SYSTOLIC)?.map((item, index) => (
                                        <>
                                            <div key={index} className="d-flex d-block p-3">
                                                <span className="w-30">{item.day}</span>
                                                <span className="w-35 text-center">{item[MatrixConstants.BLOOD_PRESSURE_SYSTOLIC]}</span>
                                                <span className="w-35 text-center">{item[MatrixConstants.BLOOD_PRESSURE_DIASTOLIC]}</span>
                                            </div>
                                            <hr className="m-0" />
                                        </>
                                    )) : <div className="d-flex d-block p-3">
                                    <span className="w-100 fw-bold text-center">No Data Found</span>
                                </div>
                                }
                            </div>
                        </div>
                    </div>
                </div>
                </div>
                <div className="p-5 bg-gray-FAF rounded-3" ref={a1cRef}>
                <h1 className="h5">A1C</h1>
                <div className="row g-3 mb-4">
                    <div className="col-12 col-md-12 col-lg-9 col-xl-7">
                        <div className="row g-3">
                            <MatrixCard 
                                type="A1C" 
                                value={getMetricValue(MatrixConstants.A1C)}
                                predicted={undefined}
                                lastUpdated={getLastUpdated(MatrixConstants.A1C)} 
                            />
                            <MatrixCard 
                                type="A1C_DIFF_LAST_READING" 
                                value={getDifferencePercentageOfLast2Readings(MatrixConstants.A1C)}
                                predicted={undefined}
                                trend={getDifferencePercentageOfLast2Readings(MatrixConstants.A1C) > 0 ? 'up' : 'down'}
                                lastUpdated={getLastUpdated(MatrixConstants.A1C)}  
                            />
                        </div>
                    </div>
                </div>
                <div className="row g-3">    
                    <div className="col-12 col-lg-5">
                        <div className="sidebar fs-12">
                            <div className="sidebar__header">
                                <div className="d-flex">
                                    <strong className="w-80"> Date/Time </strong>
                                    <strong className="w-20 text-center">  A1C </strong>
                                </div>
                            </div>
                            <div className="sidebar__content p-0 overflow-y-auto max-h-16">
                                {
                                    chartGridData.get(MatrixConstants.A1C)?.length ? chartGridData.get(MatrixConstants.A1C)?.map((item, index) => (
                                        <>
                                            <div key={index} className="d-flex d-block p-3">
                                                <span className="w-80">{item.day}</span>
                                                <span className="w-20 text-center">{item[MatrixConstants.A1C]}</span>
                                            </div>
                                            <hr className="m-0" />
                                        </>
                                    )) : <div className="d-flex d-block p-3">
                                    <span className="w-100 fw-bold text-center">No Data Found</span>
                                </div>
                                }
                            </div>
                        </div>
                    </div>
                </div>
                </div>
            </div>
        </div>
        }
    </>
    )
}